/**
 * Coding Intelligence -- NCCI / MUE / modifier checks on a code list.
 *
 * Route: /CodingIntelligence
 *
 * Supports two engines:
 *   v4 (default) -- deterministic scrubCodes
 *   v5           -- natural-language SNN router + LLM consolidation process map
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WorkflowLayout,
  FileDropZone,
  ResultsPanel,
  Icon,
  ExampleModal,
  getWorkflowModule,
} from '../../components/workflow';
import { scrubCodes } from '../../services/suiteApi';
import { getSampleById } from '../../fixtures/testCases';
import { CODING_INTELLIGENCE_EXAMPLE } from '../../fixtures/exampleInputs';
import './suite.css';

const KIND_META = {
  ncci: { label: 'NCCI / CCI' },
  mue:  { label: 'MUE' },
  global_period: { label: 'Global period' },
};

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

function summariseResult(codes, result, sample) {
  if (!result) return '';
  const issues = result.issues || [];
  const counts = issues.reduce((acc, i) => { acc[i.severity || 'review'] = (acc[i.severity || 'review'] || 0) + 1; return acc; }, {});
  const top = issues.slice(0, 6).map((i) => `${(i.severity || 'review').toUpperCase()} · ${KIND_META[i.kind]?.label || i.kind} — ${i.message}${i.fix ? ` (fix: ${i.fix})` : ''}`).join('\n');
  return [
    `CODING INTELLIGENCE — ${sample ? `sample: ${sample.title}` : 'code list'}`,
    `Codes: ${codes}`,
    `Issues: ${issues.length} total (${counts.block || 0} blocking, ${counts.review || 0} to review)`,
    `NCCI pairs checked: ${(result.ncci_pairs || []).length}`,
    `MUE caps applied: ${Object.keys(result.mue_caps || {}).length}`,
    '',
    top || 'No issues flagged.',
  ].join('\n');
}

// ----------- V5 Report Renderer -----------

const SEV_COLORS = { error: '#dc2626', warning: '#d97706', info: '#2563eb' };
const REC_COLORS = { SUBMIT: '#16a34a', CORRECTION_NEEDED: '#dc2626', REVIEW_REQUIRED: '#d97706' };

function V5CodingReport({ report }) {
  if (!report) return null;
  const rec = report.overallRecommendation || 'REVIEW_REQUIRED';
  return (
    <div style={{ fontSize: 13 }}>
      {/* Overall */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ padding: '3px 10px', borderRadius: 6, fontWeight: 700, fontSize: 12, color: '#fff', background: REC_COLORS[rec] || '#666' }}>{rec.replace(/_/g, ' ')}</span>
        <span style={{ color: 'var(--dm-ink)' }}>{report.summary}</span>
      </div>

      {/* Findings */}
      {(report.findings || []).length > 0 && (
        <details open style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Findings ({report.findings.length})</summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
            {report.findings.map((f, i) => (
              <li key={i} style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 700, color: SEV_COLORS[f.severity] || '#666', textTransform: 'uppercase', fontSize: 11, marginRight: 6 }}>{f.severity}</span>
                <b>{f.type?.replace(/_/g, ' ')}</b>
                {f.affectedCodes?.length > 0 && <span style={{ color: 'var(--dm-muted)', marginLeft: 6 }}>[{f.affectedCodes.join(', ')}]</span>}
                <div style={{ marginTop: 2 }}>{f.description}</div>
                {f.policyBasis && <div style={{ marginTop: 2, color: '#4338ca', fontSize: 12 }}><b>Policy:</b> {f.policyBasis}</div>}
                {f.recommendedCorrection && <div style={{ marginTop: 2, fontSize: 12 }}><b>Fix:</b> {f.recommendedCorrection}</div>}
                {f.alternativeAction && <div style={{ marginTop: 2, fontSize: 12, color: 'var(--dm-muted)' }}><b>Alt:</b> {f.alternativeAction}</div>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* NCCI Detail */}
      {(report.ncciFindingsDetail || []).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>NCCI Edit Details</summary>
          <table style={{ width: '100%', fontSize: 12, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--dm-border)' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>Col 1</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Col 2</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Edit Type</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Mod Ind</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Resolution</th>
            </tr></thead>
            <tbody>
              {report.ncciFindingsDetail.map((n, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--dm-border)' }}>
                  <td style={{ padding: 4 }}>{n.column1}</td>
                  <td style={{ padding: 4 }}>{n.column2}</td>
                  <td style={{ padding: 4 }}>{n.editType}</td>
                  <td style={{ padding: 4 }}>{n.modifierIndicator}</td>
                  <td style={{ padding: 4 }}>{n.resolution}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* MUE Detail */}
      {(report.mueFindingsDetail || []).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>MUE Details</summary>
          <table style={{ width: '100%', fontSize: 12, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--dm-border)' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>CPT</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Billed</th>
              <th style={{ textAlign: 'left', padding: 4 }}>MUE Limit</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Source</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Resolution</th>
            </tr></thead>
            <tbody>
              {report.mueFindingsDetail.map((m, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--dm-border)' }}>
                  <td style={{ padding: 4 }}>{m.cptCode}</td>
                  <td style={{ padding: 4 }}>{m.billedUnits}</td>
                  <td style={{ padding: 4 }}>{m.mueLimit}</td>
                  <td style={{ padding: 4 }}>{m.mueSource}</td>
                  <td style={{ padding: 4 }}>{m.resolution}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* Modifier Review */}
      {(report.modifierReview || []).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Modifier Review</summary>
          <table style={{ width: '100%', fontSize: 12, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--dm-border)' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>CPT</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Modifier</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Supported</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Basis</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Recommendation</th>
            </tr></thead>
            <tbody>
              {report.modifierReview.map((mr, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--dm-border)' }}>
                  <td style={{ padding: 4 }}>{mr.cptCode}</td>
                  <td style={{ padding: 4 }}>{mr.modifier}</td>
                  <td style={{ padding: 4 }}>{mr.isSupported === true ? 'Yes' : mr.isSupported === false ? 'No' : 'Unknown'}</td>
                  <td style={{ padding: 4 }}>{mr.supportBasis}</td>
                  <td style={{ padding: 4 }}>{mr.recommendation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* Diagnosis Support */}
      {(report.diagnosisSupport || []).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Diagnosis Support</summary>
          <table style={{ width: '100%', fontSize: 12, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--dm-border)' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>CPT</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Submitted Dx</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Adequate</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Gap</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Recommendation</th>
            </tr></thead>
            <tbody>
              {report.diagnosisSupport.map((ds, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--dm-border)' }}>
                  <td style={{ padding: 4 }}>{ds.cptCode}</td>
                  <td style={{ padding: 4 }}>{(ds.submittedDx || []).join(', ')}</td>
                  <td style={{ padding: 4 }}>{ds.adequate === true ? 'Yes' : ds.adequate === false ? 'No' : 'Unknown'}</td>
                  <td style={{ padding: 4 }}>{ds.gap}</td>
                  <td style={{ padding: 4 }}>{ds.recommendation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* Corrected Claim Example */}
      {report.correctedClaimExample?.lines?.length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Corrected Claim ({report.correctedClaimExample.scenario})</summary>
          <table style={{ width: '100%', fontSize: 12, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--dm-border)' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>CPT</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Units</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Modifiers</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Action</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Notes</th>
            </tr></thead>
            <tbody>
              {report.correctedClaimExample.lines.map((ln, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--dm-border)' }}>
                  <td style={{ padding: 4 }}>{ln.cptCode}</td>
                  <td style={{ padding: 4 }}>{ln.units}</td>
                  <td style={{ padding: 4 }}>{(ln.modifiers || []).join(', ') || '-'}</td>
                  <td style={{ padding: 4, fontWeight: 700, color: ln.action === 'REMOVE' ? '#dc2626' : ln.action === 'MODIFY' ? '#d97706' : ln.action === 'ADD' ? '#16a34a' : 'inherit' }}>{ln.action}</td>
                  <td style={{ padding: 4 }}>{ln.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* Alternative Corrected Claim */}
      {report.alternativeCorrectedClaim?.lines?.length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Alternative Correction ({report.alternativeCorrectedClaim.scenario})</summary>
          <table style={{ width: '100%', fontSize: 12, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--dm-border)' }}>
              <th style={{ textAlign: 'left', padding: 4 }}>CPT</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Units</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Modifiers</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Action</th>
              <th style={{ textAlign: 'left', padding: 4 }}>Notes</th>
            </tr></thead>
            <tbody>
              {report.alternativeCorrectedClaim.lines.map((ln, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--dm-border)' }}>
                  <td style={{ padding: 4 }}>{ln.cptCode}</td>
                  <td style={{ padding: 4 }}>{ln.units}</td>
                  <td style={{ padding: 4 }}>{(ln.modifiers || []).join(', ') || '-'}</td>
                  <td style={{ padding: 4, fontWeight: 700, color: ln.action === 'REMOVE' ? '#dc2626' : ln.action === 'MODIFY' ? '#d97706' : ln.action === 'ADD' ? '#16a34a' : 'inherit' }}>{ln.action}</td>
                  <td style={{ padding: 4 }}>{ln.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* NL Analysis */}
      {report.naturalLanguageAnalysis && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontWeight: 700, cursor: 'pointer' }}>Detailed Analysis</summary>
          <div style={{ whiteSpace: 'pre-wrap', marginTop: 6, lineHeight: 1.5, color: 'var(--dm-ink)' }}>
            {report.naturalLanguageAnalysis}
          </div>
        </details>
      )}
    </div>
  );
}

// ----------- Main Page Component -----------

export default function CodingIntelligencePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleDef = getWorkflowModule('coding-intelligence');

  const [engine, setEngine] = useState('v5');
  const [files, setFiles] = useState([]);
  const [codes, setCodes] = useState('');
  const [icd, setIcd] = useState('');
  const [units, setUnits] = useState('');
  const [modifiers, setModifiers] = useState('');
  const [clinicalText, setClinicalText] = useState('');
  const [showExample, setShowExample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busySampleId, setBusySampleId] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [v5Report, setV5Report] = useState(null);
  const [sample, setSample] = useState(null);
  const printRef = useRef(null);

  const sampleId = searchParams.get('sample');

  const run = async ({ codes: c, icd: i, units: u, modifiers: m, clinicalText: ct } = {}) => {
    setBusy(true); setError(''); setResult(null); setV5Report(null);
    const textVal = ct ?? clinicalText;
    try {
      if (engine === 'v5' && textVal.trim()) {
        const data = await scrubCodes({ engine: 'v5', clinicalText: textVal.trim() });
        if (!data?.success) throw new Error(data?.message || 'Check failed');
        setV5Report(data.data);
      } else {
        const codeList = (c ?? codes).split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
        const icdList  = (i ?? icd).split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
        const unitMap  = (u ?? units).split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
        const modList  = (m ?? modifiers).split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
        const serviceLines = codeList.map((cd, idx) => ({
          procedureCode: cd,
          units: Number(unitMap[idx] || 1),
          modifiers: modList.length ? modList.filter(Boolean) : [],
        }));
        const data = await scrubCodes({ codes: codeList, icd: icdList, serviceLines });
        if (!data?.success) throw new Error(data?.message || 'Check failed');
        setResult(data.data);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onPickSample = async (s) => {
    setBusySampleId(s.id);
    setSample(s);
    setCodes(s.payload.codes || '');
    setIcd(s.payload.icd || '');
    setUnits(s.payload.units || '');
    setModifiers(s.payload.modifiers || '');
    if (s.payload.clinicalText) setClinicalText(s.payload.clinicalText);
    await run({
      codes: s.payload.codes, icd: s.payload.icd,
      units: s.payload.units, modifiers: s.payload.modifiers,
      clinicalText: s.payload.clinicalText,
    });
    setBusySampleId(null);
  };

  useEffect(() => {
    if (!sampleId) return;
    const s = getSampleById(sampleId);
    if (s && s.moduleId === 'coding-intelligence') onPickSample(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleId]);

  useEffect(() => {
    if (!files.length) return;
    (async () => {
      try {
        const txt = await readFileAsText(files[0]);
        const lines = txt.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        const codeList = [];
        const unitList = [];
        for (const l of lines) {
          const cols = l.split(/[,;\t]/).map((c) => c.trim());
          const cd = cols[0];
          if (!cd) continue;
          if (/^(code|cpt|hcpcs)$/i.test(cd)) continue;
          codeList.push(cd);
          if (cols[1] && /^\d+$/.test(cols[1])) unitList.push(cols[1]);
        }
        if (codeList.length) {
          setCodes(codeList.join(', '));
          if (unitList.length) setUnits(unitList.join(', '));
          setError('');
        }
      } catch (err) {
        setError(err?.message || 'Could not read file');
      }
    })();
  }, [files]);

  const onRun = () => {
    setSample(null);
    if (sampleId) setSearchParams({});
    run();
  };

  const currentStep = (result || v5Report) ? 'results' : (codes.trim() || clinicalText.trim() ? 'run' : 'choose');
  const chatContext = useMemo(() => summariseResult(codes, result, sample), [codes, result, sample]);

  return (
    <WorkflowLayout
      module={moduleDef}
      currentStep={currentStep}
      caseContext={chatContext}
    >

      <section className="wf-step-card">
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title">
            <span className="wf-step-card__num">2</span>
            Enter the codes
          </h2>
        </header>
        <p className="wf-step-card__hint">{moduleDef.uploadHint}</p>

        {engine === 'v5' && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Clinical Claim Description (natural language)
              </label>
              <button type="button" className="example-link" onClick={() => setShowExample(true)}>View Example</button>
            </div>
            <textarea
              value={clinicalText}
              onChange={(e) => setClinicalText(e.target.value)}
              placeholder="Paste or type the full claim description with service lines, CPT/HCPCS codes, units, modifiers, and diagnosis codes..."
              rows={8}
              style={{ marginTop: 6, width: '100%', padding: 10, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 13, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5, boxSizing: 'border-box' }}
            />
          </div>
        )}

        {engine === 'v4' && (
          <>
            <FileDropZone
              files={files}
              onFiles={setFiles}
              accept={moduleDef.accepts}
              multiple={false}
              hint="CSV / TXT with one CPT per line (optional units in column 2)."
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                CPT / HCPCS
                <input value={codes} onChange={(e) => setCodes(e.target.value)} style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                ICD-10 (optional)
                <input value={icd} onChange={(e) => setIcd(e.target.value)} style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Units (optional)
                <input value={units} onChange={(e) => setUnits(e.target.value)} placeholder="1, 2" style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Modifiers (optional)
                <input value={modifiers} onChange={(e) => setModifiers(e.target.value)} placeholder="25, 59" style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }} />
              </label>
            </div>
          </>
        )}

        <div className="wf-actions">
          <button type="button" className="wf-btn" disabled={busy || !clinicalText.trim()} onClick={onRun}>
            <Icon.play /> {busy ? 'Checking…' : moduleDef.runLabel}
          </button>
        </div>
        {error && <div className="wf-error">{error}</div>}
      </section>

      {v5Report && (
        <ResultsPanel
          title="Coding Intelligence Report (v5)"
          data={v5Report}
          downloadName={`coding-intelligence-v5_${sample?.id || 'check'}`}
          textReport={v5Report.naturalLanguageAnalysis || ''}
          printableRef={printRef}
        >
          <div ref={printRef}>
            <V5CodingReport report={v5Report} />
          </div>
        </ResultsPanel>
      )}

      {result && (
        <ResultsPanel
          title="Coding check results"
          data={result}
          downloadName={`coding-intelligence_${sample?.id || 'check'}`}
          textReport={chatContext}
          printableRef={printRef}
        >
          <div ref={printRef}>
            <dl className="wf-kv">
              <div><dt>Issues</dt><dd>{(result.issues || []).length}</dd></div>
              <div><dt>NCCI pairs</dt><dd>{(result.ncci_pairs || []).length}</dd></div>
              <div><dt>MUE caps</dt><dd>{Object.keys(result.mue_caps || {}).length}</dd></div>
              <div><dt>Global periods</dt><dd>{Object.keys(result.global_periods || {}).length}</dd></div>
            </dl>
            <div style={{ marginTop: 10 }}>
              {(result.issues || []).length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--dm-ink)' }}>
                  No edits flagged. This code set is clean against NCCI / MUE / global-period rules.
                </p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--dm-ink)' }}>
                  {(result.issues || []).map((i, idx) => (
                    <li key={idx} style={{ marginBottom: 6 }}>
                      <b>{(i.severity || 'review').toUpperCase()}</b>
                      &nbsp;· {KIND_META[i.kind]?.label || (i.kind || 'edit').toUpperCase()}
                      {(i.codes || []).length > 0 ? ` · ${i.codes.join(' + ')}` : ''}
                      <div style={{ color: 'var(--dm-muted)', marginTop: 2 }}>{i.message}</div>
                      {i.fix && <div style={{ color: '#1d4ed8', marginTop: 2, fontSize: 12 }}><b>Fix:</b> {i.fix}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </ResultsPanel>
      )}
      <ExampleModal open={showExample} onClose={() => setShowExample(false)} title="Example: Coding Intelligence Input" content={CODING_INTELLIGENCE_EXAMPLE} />
    </WorkflowLayout>
  );
}
